import { BOOKING_API_URL, supabase } from '../lib/supabase';

export type RefundKind = 'none' | 'full' | 'partial' | 'voucher';

export interface CancelRefundRequest {
  booking_id: string;
  refund: RefundKind;
  refund_amount_cents?: number;       // required if refund='partial'
  reason?: string | null;
  /** Force-cancel an already-arrived booking. Default false. */
  force_arrived?: boolean;
  /**
   * 'departure' (default) cancels only this departure. 'booking' cancels every
   * still-active departure under the parent booking and issues one combined
   * refund/voucher for them all.
   */
  scope?: 'departure' | 'booking';
}

export interface CancelRefundResponse {
  booking_id: string;
  booking_number: string;
  lifecycle_status: string;
  payment_status: string;
  refund: {
    applied: boolean;
    method: 'stripe' | 'cash' | 'card_terminal' | null;
    amount_cents: number | null;
    stripe_refund_id: string | null;
  } | null;
  /** Present only for refund='voucher' — the issued store-credit code. */
  voucher: {
    code: string;
    amount_cents: number;
    email_sent: boolean;
  } | null;
  // Gift card share put back on the card (full / voucher refunds).
  gift_card?:
    | { credited_cents: number; code_display: string | null; balance_after_cents: number | null }
    | { error: string }
    | null;
}

export interface CancelRefundError {
  error: string;
  message?: string;
}

export interface CancelRefundResult {
  ok: boolean;
  data: CancelRefundResponse | null;
  error: CancelRefundError | null;
  status: number;
}

export async function cancelBooking(
  body: CancelRefundRequest,
): Promise<CancelRefundResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-cancel-refund`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: {
        error: 'network_error',
        message: e instanceof Error ? e.message : String(e),
      },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as CancelRefundError) ?? {
        error: 'unknown_error',
        message: `HTTP ${res.status}`,
      },
      status: res.status,
    };
  }

  return {
    ok: true,
    data: json as CancelRefundResponse,
    error: null,
    status: res.status,
  };
}
